
import React, { useMemo, useState, useRef } from 'react';
import { Reservation, Table, AppSettings, ReservationSource } from '../types';
import { SOURCE_LABELS } from '../constants';
import { Clock, Users, X, FileDown, Calendar, Edit2, Trash2, AlignLeft, Layout, Loader2, AlertCircle, GripHorizontal, StickyNote, Phone, Store, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeNumberInput } from './MobileControls';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface ScheduleViewProps {
  viewDate: string;
  reservations: Reservation[];
  tables: Table[];
  appSettings: AppSettings;
  onUpdateReservation: (res: Reservation) => void;
  onDateChange: (date: string) => void;
}

const AUTO_COLORS = [
  { bg: '#EEF2FF', border: '#C7D2FE', text: '#4338CA' }, { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D' },
  { bg: '#FEFCE8', border: '#FEF08A', text: '#A16207' }, { bg: '#FFF1F2', border: '#FECDD3', text: '#BE123C' },
  { bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9' }, { bg: '#F0FBFF', border: '#BAE6FD', text: '#0369A1' },
  { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' }, { bg: '#FDF4FF', border: '#F5D0FE', text: '#A21CAF' },
  { bg: '#F0FDFA', border: '#99F6E4', text: '#0F766E' }, { bg: '#FFF7ED', border: '#FFEDD5', text: '#C2410C' },
];

const CustomInstagramIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const CustomLineIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);

const SourceIcon: React.FC<{ source: ReservationSource }> = ({ source }) => {
  switch (source) {
    case 'instagram': return <CustomInstagramIcon size={16} />;
    case 'line': return <CustomLineIcon size={16} />;
    case 'phone': return <Phone size={16} />;
    case 'store': return <Store size={16} />;
    default: return <Store size={16} />;
  }
};

export const ScheduleView: React.FC<ScheduleViewProps> = ({ viewDate, reservations, tables, appSettings, onUpdateReservation, onDateChange }) => {
  const [editingRes, setEditingRes] = useState<Reservation | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const startHourStr = appSettings.businessStartTime || '10:00';
  const endHourStr = appSettings.businessEndTime || '22:00';
  const startHour = parseInt(startHourStr.split(':')[0]);
  const endHour = parseInt(endHourStr.split(':')[0]);

  const hours = Array.from({ length: (endHour - startHour) * 2 + 1 }, (_, i) => {
    const h = startHour + Math.floor(i / 2);
    const m = (i % 2) * 30;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  const rowHeight = 60;
  const defaultDuration = appSettings.reservationLimits?.timeWindowMinutes || 120;
  
  const filteredReservations = useMemo(() => reservations.filter(r => r.date === viewDate && r.status !== 'cancelled'), [reservations, viewDate]);
  const assignedReservations = useMemo(() => filteredReservations.filter(r => !!r.tableId), [filteredReservations]);
  const unassignedReservations = useMemo(() => filteredReservations.filter(r => !r.tableId), [filteredReservations]);

  const getResColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AUTO_COLORS[Math.abs(hash) % AUTO_COLORS.length];
  };

  const handleDragEnd = (res: Reservation, info: any) => {
    const deltaMins = Math.round(info.offset.y / 20) * 10;
    if (!deltaMins) return;
    const [h, m] = res.time.split(':').map(Number);
    const newMins = Math.max(startHour * 60, Math.min(endHour * 60, (h * 60 + m) + deltaMins));
    onUpdateReservation({ ...res, time: `${String(Math.floor(newMins/60)).padStart(2,'0')}:${String(newMins%60).padStart(2,'0')}` });
  };

  const handleResizeEnd = (res: Reservation, info: any) => {
    const deltaMins = Math.round(info.offset.y / 20) * 10;
    const currentDuration = res.duration || defaultDuration;
    const newDuration = Math.max(20, currentDuration + deltaMins);
    
    if (newDuration !== currentDuration) {
      onUpdateReservation({ ...res, duration: newDuration });
    }
  };

  const handleGeneratePDF = async () => {
    const container = document.getElementById('pdf-export-container');
    if (!container) return;
    
    setIsGenerating(true);

    const pdfRowHeight = 65; 
    const totalMinutes = (endHour - startHour) * 60 + 30;
    const pdfTotalHeight = (totalMinutes / 30) * pdfRowHeight;
    const tableWidth = 100 / Math.max(1, tables.length);

    container.innerHTML = `
      <div style="padding: 10mm; background: white; width: 210mm; font-family: 'Zen Maru Gothic', sans-serif; box-sizing: border-box; position: relative; min-height: 297mm;">
        <div style="display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 5px solid #1e293b; padding-bottom: 12px; margin-bottom: 25px;">
          <h1 style="margin: 0; font-size: 26px; font-weight: 900; color: #1e293b; letter-spacing: -0.02em;">${appSettings.storeName || 'Reservation'} 予約台帳</h1>
          <div style="text-align: right; font-size: 18px; font-weight: 900; color: #4f46e5;">
            ${viewDate}
          </div>
        </div>
        <div style="position: relative; border: 1.5px solid #cbd5e1; display: flex;">
          <div style="width: 55px; border-right: 1.5px solid #cbd5e1; background: #f8fafc; shrink-0;">
             <div style="height: 40px; border-bottom: 1.5px solid #cbd5e1;"></div>
             ${hours.map(time => `
               <div style="height: ${pdfRowHeight}px; border-bottom: 1px solid #e2e8f0; position: relative;">
                 <span style="position: absolute; top: -8px; left: 0; right: 0; text-align: center; font-size: 10px; font-weight: 900; color: #64748b; font-family: monospace;">
                   ${time}
                 </span>
               </div>
             `).join('')}
          </div>
          <div style="flex: 1; display: flex; position: relative;">
            ${tables.map(table => `
              <div style="width: ${tableWidth}%; border-right: 1px solid #cbd5e1; position: relative; min-height: ${pdfTotalHeight + 40}px;">
                <div style="height: 40px; border-bottom: 1.5px solid #cbd5e1; display: flex; align-items: center; justify-content: center; background: #f1f5f9; color: #1e293b; z-index: 10;">
                  <span style="font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em;">${table.name}</span>
                </div>
                <div style="position: absolute; top: 40px; left: 0; right: 0; bottom: 0; z-index: 1;">
                  ${hours.map((_, i) => `<div style="height: ${pdfRowHeight}px; border-bottom: 1px solid #f1f5f9; ${i % 2 === 0 ? 'background: rgba(248, 250, 252, 0.6);' : ''}"></div>`).join('')}
                </div>
                ${assignedReservations.filter(r => r.tableId === table.id).map(res => {
                  const [h, m] = res.time.split(':').map(Number);
                  const startMins = h * 60 + m - startHour * 60;
                  const top = (startMins / 30) * pdfRowHeight + 40;
                  const height = ((res.duration || defaultDuration) / 30) * pdfRowHeight;
                  const colors = getResColor(res.name);
                  return `
                    <div style="position: absolute; top: ${top}px; left: 3px; right: 3px; height: ${height}px; background-color: ${colors.bg}; border: 1.5px solid ${colors.border}; border-left: 7px solid ${colors.text}; border-radius: 12px; padding: 8px 10px; z-index: 1000; box-sizing: border-box; overflow: hidden; display: flex; flex-direction: column;">
                      <div style="flex-shrink: 0; margin-bottom: 4px;">
                        <div style="font-size: 15px; font-weight: 900; color: #0f172a; line-height: 1.1; word-break: break-all; display: block; width: 100%; border-bottom: 2px solid rgba(0,0,0,0.08); padding-bottom: 5px;">
                           ${res.name} 様
                        </div>
                        <div style="height: 4px; width: 100%;"></div>
                      </div>
                      <div style="font-size: 14px; font-weight: 900; color: #334155; margin-bottom: 2px; flex-shrink: 0;">👤 ${res.guests}名</div>
                      <div style="font-size: 11px; font-weight: 900; color: ${colors.text}; font-family: monospace; line-height: 1.1; margin-bottom: 4px; flex-shrink: 0;">
                        ${res.time}〜 (${res.duration || defaultDuration}分)
                      </div>
                      ${res.notes ? `<div style="flex: 1; min-height: 0; margin-top: 4px; font-size: 13px; font-weight: 900; color: #1e293b; line-height: 1.25; word-break: break-all; opacity: 0.9;">memo: ${res.notes}</div>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            `).join('')}
          </div>
        </div>
        ${unassignedReservations.length > 0 ? `
           <div style="margin-top: 40px; page-break-inside: avoid; z-index: 50;">
             <h3 style="font-size: 16px; color: #7c2d12; font-weight: 900; border-left: 8px solid #f97316; padding-left: 15px; margin-bottom: 20px;">テーブル未確定</h3>
             <div style="display: flex; flex-wrap: wrap; gap: 15px;">
               ${unassignedReservations.map(r => `
                 <div style="background: #fffbeb; border: 2.5px solid #fef3c7; padding: 18px; border-radius: 15px; width: 48%; box-sizing: border-box;">
                   <div style="font-size: 17px; font-weight: 900; color: #1e293b; border-bottom: 2px solid #fde68a; padding-bottom: 6px; margin-bottom: 10px; display: block; width: 100%;">
                      ${r.name} 様
                   </div>
                   <div style="font-size: 16px; font-weight: 900; color: #1e293b; margin-top: 4px;">👤 ${r.guests}名</div>
                   <div style="font-size: 12px; color: #b45309; margin-top: 8px; font-weight: 900;">${r.time}〜 (${r.duration || defaultDuration}分)</div>
                   ${r.notes ? `<div style="font-size: 14px; font-weight: 900; color: #92400e; margin-top: 10px; opacity: 0.9;">memo: ${r.notes}</div>` : ''}
                 </div>
               `).join('')}
             </div>
           </div>
        ` : ''}
      </div>
    `;

    try {
      const canvas = await html2canvas(container, { 
        scale: 2, 
        useCORS: true, 
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // 直接ファイルを保存して、ブラウザにダウンロード処理を任せる（確実な拡張子付与）
      pdf.save(`Ledger_${viewDate}.pdf`);

      // iOS等でプレビューが必要な場合用のBlob URL fallback
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      setTimeout(() => {
        window.open(blobUrl, '_blank');
      }, 500);

    } catch (err) {
      console.error(err);
      alert('PDFの生成中にエラーが発生しました。');
    } finally {
      setIsGenerating(false);
      container.innerHTML = '';
    }
  };

  const [editHour, setEditHour] = useState(12);
  const [editMinute, setEditMinute] = useState(0);

  const sources: ReservationSource[] = ['instagram', 'line', 'phone', 'store'];

  return (
    <div className="h-full flex flex-col relative overflow-hidden">
       <div id="pdf-export-container" className="fixed top-0 left-[-3000px] z-[-1000] pointer-events-none"></div>
       <div className="flex justify-between items-center mb-4 px-2 shrink-0">
          <div className="relative group cursor-pointer active:scale-95 transition-all">
            <h2 className="text-lg font-black flex items-center gap-2 py-1 px-2 rounded-xl group-hover:bg-stone-100 transition-colors">
              <Calendar size={22} className="text-indigo-500" />
              <span>{new Date(viewDate).toLocaleDateString('ja-JP', {year:'numeric', month:'long', day:'numeric', weekday:'short'})}</span>
              <ChevronDown size={16} className="text-stone-300 group-hover:text-indigo-400 transition-colors" />
            </h2>
            {/* 透明な日付入力を重ねることで、テキストタップでカレンダーを開く */}
            <input 
              type="date" 
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10" 
              value={viewDate} 
              onChange={e => onDateChange(e.target.value)} 
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={handleGeneratePDF} disabled={isGenerating} className="p-3 bg-white text-rose-600 rounded-2xl border border-rose-100 shadow-sm flex items-center gap-2 active:scale-95 transition-all">
              {isGenerating ? <Loader2 size={20} className="animate-spin" /> : <FileDown size={20} />}
              <span className="text-xs font-black hidden sm:inline">{isGenerating ? "生成中..." : "PDF保存/表示"}</span>
            </button>
          </div>
       </div>

       <div className="flex-1 overflow-auto bg-white rounded-t-[2.5rem] shadow-sm border border-stone-100 relative no-scrollbar overscroll-contain">
          <div className="flex min-w-full relative">
             <div className="sticky top-0 left-0 z-40 bg-white w-16 h-12 border-b border-r border-stone-100" />
             <div className="w-16 shrink-0 sticky left-0 bg-white/95 backdrop-blur-md z-30 border-r border-stone-100">
                <div className="h-12" />
                {hours.map(time => (<div key={time} className="text-[10px] font-bold text-stone-300 font-mono flex items-start justify-center" style={{ height: rowHeight }}><span className={`-mt-2 px-1 rounded ${time.endsWith(':00') ? 'bg-stone-100 text-stone-500' : ''}`}>{time}</span></div>))}
             </div>
             <div className="flex-1 flex min-w-[800px] relative">
                {tables.map(table => (
                   <div key={table.id} className="flex-1 min-w-[120px] relative border-l border-stone-50">
                      <div className="h-12 flex items-center justify-center sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-stone-100 shadow-sm"><span className="font-black text-[10px] text-stone-400 uppercase tracking-widest">{table.name}</span></div>
                      <div className="relative" style={{ height: hours.length * rowHeight }}>
                         {hours.map((_, i) => <div key={i} className="border-b border-stone-50/50" style={{ height: rowHeight }} />)}
                         {assignedReservations.filter(r => r.tableId === table.id).map(res => {
                              const resColors = getResColor(res.name);
                              const top = (parseInt(res.time.split(':')[0]) * 60 + parseInt(res.time.split(':')[1]) - startHour * 60) / 30 * rowHeight;
                              const height = (res.duration || defaultDuration) / 30 * rowHeight;
                              return (
                                <motion.div 
                                  key={res.id} 
                                  drag="y" 
                                  dragConstraints={{ top: 0, bottom: hours.length * rowHeight }} 
                                  dragElastic={0.02} 
                                  dragMomentum={false} 
                                  onDragEnd={(_, info) => handleDragEnd(res, info)} 
                                  className="absolute left-1 right-1 rounded-2xl shadow-lg p-2.5 flex flex-col overflow-visible border-l-8 z-10 cursor-grab active:cursor-grabbing group" 
                                  style={{ top: `${top}px`, height: `${height}px`, backgroundColor: resColors.bg, borderColor: resColors.text }}
                                >
                                   <div className="flex justify-between items-start gap-1 shrink-0 mb-1">
                                      <div className="flex-1 flex flex-col min-w-0">
                                        <div className="text-base font-black text-stone-800 leading-[1.1] py-0.5 break-all line-clamp-5 overflow-hidden">
                                          {res.name} 様
                                        </div>
                                      </div>
                                      <button onClick={() => { setEditHour(parseInt(res.time.split(':')[0])); setEditMinute(parseInt(res.time.split(':')[1])); setEditingRes(res); }} className="p-1 bg-white/70 rounded-lg shadow-sm hover:bg-white transition-colors shrink-0 -mt-0.5 -mr-0.5"><Edit2 size={12} strokeWidth={3}/></button>
                                   </div>
                                   <div className="h-[1.5px] w-full bg-black/10 my-1 shrink-0"></div>
                                   <div className="text-base font-black text-stone-700 flex items-center gap-1 shrink-0 mb-1">
                                      <Users size={12} strokeWidth={3}/> {res.guests}名
                                   </div>
                                   <div className="text-[10px] text-stone-600 font-black font-mono mb-1 flex flex-col gap-0.5 shrink-0">
                                     <div className="flex items-center gap-1"><Clock size={10}/> {res.time} 〜</div>
                                     <div className="opacity-60">({(res.duration || defaultDuration)}分)</div>
                                   </div>
                                   {res.notes && (
                                     <div className="flex-1 min-h-0 mt-1 mb-2 text-base leading-[1.25] font-black text-stone-800 overflow-hidden break-all line-clamp-6 relative">
                                       <span className="text-[10px] uppercase opacity-40 block mb-0.5">memo:</span>
                                       {res.notes}
                                     </div>
                                   )}
                                   <motion.div 
                                     drag="y"
                                     dragMomentum={false}
                                     onDragEnd={(_, info) => handleResizeEnd(res, info)}
                                     className="absolute -bottom-3 left-6 right-6 h-8 cursor-ns-resize flex items-center justify-center z-20 group-hover:opacity-100 opacity-0 transition-opacity"
                                   >
                                      <div className="w-14 h-1.5 bg-stone-400/40 rounded-full group-hover:bg-orange-500/80 shadow-sm border border-white"></div>
                                   </motion.div>
                                </motion.div>
                              );
                         })}
                      </div>
                   </div>
                ))}
             </div>
          </div>
       </div>

       {unassignedReservations.length > 0 && (
         <div className="h-44 bg-amber-50/50 border-t-2 border-amber-200/50 shrink-0 p-4 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 mb-3">
               <AlertCircle size={16} className="text-amber-500 animate-pulse" />
               <h3 className="text-xs font-black text-amber-700 uppercase tracking-widest">テーブル未確定 ({unassignedReservations.length})</h3>
            </div>
            <div className="flex-1 flex gap-3 overflow-x-auto no-scrollbar pb-2 overscroll-contain">
               {unassignedReservations.map(res => (
                  <button 
                    key={res.id} 
                    onClick={() => { setEditHour(parseInt(res.time.split(':')[0])); setEditMinute(parseInt(res.time.split(':')[1])); setEditingRes(res); }}
                    className="shrink-0 w-52 bg-white rounded-2xl p-4 border-b-4 border-amber-400 shadow-md flex flex-col text-left active:scale-95 transition-all hover:shadow-lg"
                  >
                    <div className="font-black text-stone-900 text-base mb-1 line-clamp-3 leading-[1.15]">{res.name} 様</div>
                    <div className="h-[1px] w-full bg-stone-100 my-2"></div>
                    <div className="font-black text-stone-700 text-base mb-2">👤 {res.guests}名</div>
                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex flex-col">
                        <span className="text-xs font-mono font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg w-fit">{res.time}〜</span>
                        <span className="text-[10px] text-stone-400 font-bold ml-1">({res.duration || defaultDuration}分)</span>
                      </div>
                    </div>
                    {res.notes && <div className="mt-2 text-base font-black text-stone-600 italic truncate border-t border-stone-50 pt-2">memo: {res.notes}</div>}
                  </button>
               ))}
            </div>
         </div>
       )}

       <AnimatePresence>
          {editingRes && (
             <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-white rounded-[2.5rem] p-6 w-full max-w-md shadow-2xl flex flex-col max-h-[95vh] overflow-y-auto no-scrollbar">
                   <div className="flex justify-between items-center mb-6 shrink-0">
                      <h3 className="font-black text-lg">予約の詳細変更</h3>
                      <button onClick={() => setEditingRes(null)} className="p-2 bg-stone-100 rounded-full hover:bg-stone-200 transition-colors"><X size={20}/></button>
                   </div>
                   <form onSubmit={e => { e.preventDefault(); onUpdateReservation({ ...editingRes, time: `${String(editHour).padStart(2,'0')}:${String(editMinute).padStart(2,'0')}` }); setEditingRes(null); }} className="space-y-6 flex-1 pr-1">
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-1">
                            <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase">Name</label>
                            <input required className="w-full bg-stone-50 rounded-2xl h-12 px-4 font-bold border-none focus:ring-2 focus:ring-indigo-100 outline-none" value={editingRes.name} onChange={e => setEditingRes({...editingRes, name: e.target.value})} />
                         </div>
                         <div className="space-y-1">
                            <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase">Table</label>
                            <select className="w-full bg-stone-50 rounded-2xl h-12 px-3 font-bold text-sm border-none focus:ring-2 focus:ring-indigo-100 outline-none" value={editingRes.tableId || ''} onChange={e => setEditingRes({...editingRes, tableId: e.target.value})}>
                               <option value="">未確定</option>
                               {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                         </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase">予約経路</label>
                        <div className="grid grid-cols-4 gap-2">
                          {sources.map(s => {
                            const label = SOURCE_LABELS[s];
                            const isSelected = editingRes.source === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setEditingRes({...editingRes, source: s})}
                                className={`flex flex-col items-center justify-center py-2 rounded-xl border transition-all ${
                                  isSelected 
                                    ? 'bg-stone-800 text-white border-stone-800 shadow-md scale-105' 
                                    : 'bg-stone-50 border-stone-100 text-stone-400 hover:border-stone-200'
                                }`}
                              >
                                <SourceIcon source={s} />
                                <span className="text-[10px] font-bold mt-1">{label.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex gap-3">
                         <SwipeNumberInput label="時" value={editHour} onChange={setEditHour} min={0} max={23} unit="時" />
                         <SwipeNumberInput label="分" value={editMinute} onChange={setEditMinute} min={0} max={59} step={10} format={v=>v.toString().padStart(2,'0')} unit="分" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase text-center block">Stay Duration</label>
                        <SwipeNumberInput value={editingRes.duration || defaultDuration} onChange={v => setEditingRes({...editingRes, duration: v})} min={20} max={1440} step={10} unit="分" />
                      </div>
                      <textarea className="w-full bg-stone-50 rounded-2xl p-4 h-24 resize-none text-sm font-medium border-none focus:ring-2 focus:ring-indigo-100 outline-none" placeholder="メモ..." value={editingRes.notes || ''} onChange={e => setEditingRes({...editingRes, notes: e.target.value})} />
                      <div className="flex gap-3 pt-2 shrink-0">
                         <button type="button" onClick={() => setEditingRes(null)} className="flex-1 bg-stone-100 text-stone-500 h-14 rounded-2xl font-black transition-colors active:scale-95">閉じる</button>
                         <button type="submit" className="flex-[2] bg-stone-800 text-white h-14 rounded-2xl font-black shadow-lg hover:bg-stone-700 transition-colors active:scale-95">変更を保存</button>
                      </div>
                   </form>
                </motion.div>
             </div>
          )}
       </AnimatePresence>
    </div>
  );
};
